import React, { useEffect, useMemo, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'

import { useFetchDeliveryRegionsQuery } from '../../../redux-toolkits/user/user.slice'
import { DeliveryRegionsQuery } from '../../../redux-toolkits/locations/locations.type'
import { ArticleLocation } from '../../../redux-toolkits/lists/list.types'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = ArticleLocation

export const SelectLocation = ({
  maxSelection,
  open,
  locations,
  setLocations,
}: {
  maxSelection?: number
  open?: boolean
  locations: V[]
  setLocations: (locations: V[]) => void
}) => {
  const [queryParam, setQueryParam] = useState<DeliveryRegionsQuery>({
    page: 1,
    limit: 1000,
    search: undefined,
    isAvailable: true,
  })

  const { data, isFetching } = useFetchDeliveryRegionsQuery(
    {
      ...queryParam,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  const { data: fulfilmentCentresData, isFetching: fetchingCentres } =
    useGetFulfilmentCentersQuery(
      {
        page: 1,
        limit: 1000,
        search: queryParam.search,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !open,
      },
    )

  const availableLocations = useMemo(() => {
    const docs = data?.data?.docs ?? []
    const regionNameById = new Map(docs.map((item) => [item._id, item.name]))

    const deliveryRegions = docs
      .filter((item) => item.isAvailable)
      .map((item) => {
        const parentRegion =
          typeof item.parentRegion === 'object' ? item.parentRegion : undefined
        const parentRegionName =
          parentRegion?.name ??
          (typeof item.parentRegion === 'string'
            ? regionNameById.get(item.parentRegion)
            : undefined)

        return {
          ...item,
          locationType: 'delivery' as const,
          description: item.parentRegion
            ? `Sub-region${
                parentRegionName ? ` under ${parentRegionName}` : ''
              }`
            : 'Parent region',
        }
      })
      .sort((a, b) => {
        const aIsSubRegion = !!a.parentRegion
        const bIsSubRegion = !!b.parentRegion
        if (aIsSubRegion !== bIsSubRegion) {
          return aIsSubRegion ? 1 : -1
        }

        return a.name.localeCompare(b.name)
      })

    const pickupLocations = (fulfilmentCentresData?.data?.docs ?? [])
      .map((centre) => ({
        ...centre,
        locationType: 'pickup' as const,
        description: 'Pickup location',
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return [...pickupLocations, ...deliveryRegions]
  }, [data?.data?.docs, fulfilmentCentresData?.data?.docs])

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0 z-10">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search === '' ? undefined : search,
              page: 1,
            })
          }}
          placeholder="Search locations"
          className="!max-w-full"
        />
      </div>

      <div className="w-full flex-1 overflow-y-scroll">
        <TableComponent
          rows={availableLocations.map((item) => {
            return {
              id: item._id,
              content: [
                <Checkbox
                  className="!gap-6 !w-full"
                  disabled={
                    maxSelection !== undefined &&
                    locations.length >= maxSelection &&
                    locations.length != 1 &&
                    !locations.map((c) => c._id).includes(item._id)
                  }
                  checkStatus={
                    locations.map((c) => c._id)?.includes(item._id) ?? false
                  }
                  handleChange={(checked) => {
                    if (checked) {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(locations),
                      )
                      if (
                        maxSelection !== undefined &&
                        dataCopy.length >= maxSelection
                      ) {
                        dataCopy.shift()
                      }
                      dataCopy.push(item)

                      setLocations(dataCopy)
                    } else {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(locations),
                      )
                      setLocations(
                        dataCopy.filter(
                          (singleData) => item._id !== singleData._id,
                        ),
                      )
                    }
                  }}
                  key={item._id}
                  text={
                    <div>
                      <p className="flex-1 text-base text-black font-normal">
                        {item.name}
                      </p>

                      <p className="text-sm text-sec-black">
                        {item.description}
                      </p>
                    </div>
                  }
                />,
              ],
            }
          })}
          loading={isFetching || fetchingCentres}
          isEmpty={availableLocations.length < 1}
        />
      </div>
    </div>
  )
}

export const SelectLocationModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: ArticleLocation[]) => void
  selectedLocations?: ArticleLocation[]
  maxSelection?: number
  loading?: boolean
  buttonText?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = (val) => {},
  selectedLocations = [],
  maxSelection,
  loading = false,
  buttonText = 'Continue',
}) => {
  const [locations, setLocations] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setLocations(selectedLocations ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback()
      }}
      title={'Select location'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectLocation
          maxSelection={maxSelection}
          open={open}
          locations={locations}
          setLocations={setLocations}
        />

        <Button
          type="button"
          primary
          label={buttonText}
          onClick={() => {
            onSuccessCallback && onSuccessCallback(locations)
          }}
          loading={loading}
          disabled={loading}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
